import { AxiosError } from 'axios';
import { ViewIcon } from 'components/Icons';
import LayoutContext from 'components/Layout/LayoutContext';
import Select from 'components/Select/select';
import { AuthContext } from 'context/authContext/AuthContext';
import { RegisterContextActions } from 'context/registerContext/RegisterContextActions';
import { SituationValues, StepIds, StepStatus } from 'helpers/enums/Assembleia';
import { getProfileByJWTRoles } from 'helpers/utils/AuthUtils';
import { isNullOrUndefined } from 'helpers/utils/HTMLUtils';
import {
  getParsedJWTAssessorAccessToken,
  JWTAssessorJson,
} from 'helpers/utils/JWTUtils';
import { getUniqueKey } from 'helpers/utils/KeyUtils';
import { useRegisterContext } from 'hooks/useRegisterContext';
import { getApis } from 'library/Apis';
import { AssessorCadastroEventoRoutePath } from 'library/Routes';
import translation from 'locales/translation';
import {
  ArrowLeftIcon,
  Button,
  ButtonThemes,
  Checkbox,
  CheckboxThemes,
  CheckCircleIcon,
  Col,
  Collapse,
  ConfirmModal,
  ControlLabel,
  ControlLabelPosition,
  EditIcon,
  IconButton,
  ModalSizes,
  Row,
  ScreenSize,
  Title,
  ToastTypes,
} from 'plataforma-fundacao-componentes';
import { Fragment, useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { putSurvey } from 'services/assessor';
import {
  AuthUserPerfilId,
  IAuthUserPerfil,
} from 'store/modules/auth/IAuthUserPerfil';
import { closeModal, openModal } from 'store/modules/ModalActions';
import { makeEditable } from '../../Cadastro';
import styles from './styles.module.scss';
import { SurveyValidation, SurveyValidatorType } from './SurveyValidation';

const surveyPrefix = translation.Assessor.Evento.Cadastro.Etapas.Pesquisa;

export default function Survey() {
  const {
    state,
    setStepId,
    setStepCompleted,
    surveyStepSetFilter,
    setStepStatus,
    changeState,
  } = useRegisterContext();
  const dispatchAction = useDispatch();
  const history = useHistory();
  const match = useRouteMatch<{ id?: string; etapa?: string }>();
  const { showToast } = useContext(LayoutContext);
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState<boolean>(false);
  // const [isDownloading, setDownloading] = useState<boolean>(false);
  const [sent, setSent] = useState<boolean>(false);
  const [editButtonLoader, setEditButtonLoader] = useState<boolean>(false);
  const completedStep =
    state.completed.find(
      (item: { step: StepIds }) => item.step === StepIds.survey
    )?.completed && state.status !== StepStatus.EM_EDICAO;
  const parsedToken: JWTAssessorJson | null = getParsedJWTAssessorAccessToken(
    user!.access_token
  );
  const perfil: IAuthUserPerfil | null = parsedToken
    ? getProfileByJWTRoles(parsedToken.roles)
    : null;

  const [validator, setValidator] = useState<SurveyValidatorType | null>(null);

  useEffect(() => {
    setValidator(SurveyValidation(state, state.survey.enabled!));
  }, [state]);

  useEffect(() => {
    if (!match.params.id) {
      setTimeout(() => {
        history.push(`${AssessorCadastroEventoRoutePath}etapas/dados`);
      }, 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match]);

  useEffect(() => {
    if (
      state.showAlert &&
      (state.status === StepStatus.EM_EDICAO ||
        state.eventStatus === SituationValues.EDITABLE)
    ) {
      if (!validator?.allIsValid()) {
        if (!sent) setSent(true);
        changeState(RegisterContextActions.SET_SHOW_ALERT, false);
        return;
      }

      const mk = getUniqueKey();

      dispatchAction(
        openModal(ConfirmModal, {
          modalKey: mk,
          size: ModalSizes.Large,
          title: surveyPrefix.Modal.Titulo,
          mobileOn: ScreenSize.xs,
          children: surveyPrefix.Modal.Subtitulo,
          onClose: () => {
            changeState(RegisterContextActions.SET_SHOW_ALERT, false);
            dispatchAction(closeModal(mk));
          },
          onCancel: () => {
            changeState(RegisterContextActions.SET_SHOW_ALERT, false);
            history.push(state.toPage);
            surveyStepSetFilter({
              enabled: state.survey.enabledOld,
              template: state.survey.templateOld,
            });
            dispatchAction(closeModal(mk));
          },
          onConfirm: () => {
            changeState(RegisterContextActions.SET_SHOW_ALERT, false);
            dispatchAction(closeModal(mk));
            handleSubmit();
          },
          language: {
            confirm: surveyPrefix.Modal.Buttons.Salvar,
            cancel: surveyPrefix.Modal.Buttons.Cancelar,
          },
        })
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.showAlert, state.survey.template]);

  const handleSubmit = () => {
    if (!sent) setSent(true);
    if (!validator?.allIsValid()) return;
    setLoading(true);

    putSurvey(
      Number(match.params.id),
      state.data.campos.cooperative,
      state.survey.template
    )
      .then(() => {
        surveyStepSetFilter({
          enabledOld: isNullOrUndefined(state.survey.enabled)
            ? false
            : state.survey.enabled,
          templateOld: state.survey.template,
        });

        setStepCompleted(
          state.completed.map((item: { step: StepIds; completed: boolean }) =>
            item.step === StepIds.survey ? { ...item, completed: true } : item
          )
        );

        if (state.showAlert) {
          history.push(state.toPage);
        } else {
          history.push(
            `${AssessorCadastroEventoRoutePath}${match.params.id}/etapas/anexo`
          );
        }
      })
      .catch(() => {
        showToast({
          pauseOnFocusLoss: true,
          theme: ToastTypes.Error,
          showStatusBar: true,
          timeout: 4000,
          label: surveyPrefix.Errors.Step,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const backStep = useCallback(() => {
    const showAlert =
      JSON.stringify(state.survey.enabled) !==
        JSON.stringify(state.survey.enabledOld) ||
      JSON.stringify(state.survey.template) !==
        JSON.stringify(state.survey.templateOld);

    changeState(RegisterContextActions.SET_SHOW_ALERT, showAlert);

    changeState(
      RegisterContextActions.SET_TO_PAGE,
      `${AssessorCadastroEventoRoutePath}${
        match.params.id
          ? `${match.params.id}/etapas/pauta`
          : `etapas/${StepIds.data}`
      }`
    );

    if (
      !showAlert &&
      (JSON.stringify(state.survey.enabled) ===
        JSON.stringify(state.survey.enabledOld) ||
        JSON.stringify(state.survey.template) ===
          JSON.stringify(state.survey.templateOld))
    ) {
      history.push(
        `${AssessorCadastroEventoRoutePath}${match.params.id}/etapas/pauta`
      );
    } else {
      if (!sent) setSent(true);
    }
  }, [
    changeState,
    history,
    match.params.id,
    sent,
    state.survey.enabled,
    state.survey.enabledOld,
    state.survey.template,
    state.survey.templateOld,
  ]);

  useEffect(() => {
    setStepId(StepIds.survey);

    return () => {
      setSent(false);
      setLoading(false);
      surveyStepSetFilter({ template: state.survey.templateOld ?? undefined });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!state.survey.enabled) {
      surveyStepSetFilter({ template: undefined, templateOld: undefined });
    } else {
      if (!isNullOrUndefined(state.survey.templateOld)) {
        surveyStepSetFilter({
          template: state.survey.templateOld ?? undefined,
        });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.survey.enabled]);

  useEffect(() => {
    const element = document.getElementById('select-template');

    if (element) {
      const inputElement: HTMLInputElement | null = element.querySelector(
        'input#select-template-input'
      );

      if (inputElement) {
        const isTemplateValid = state.survey.templates.some(
          (e: { oidTemplatePesquisa: string; flgTemplatePadrao: boolean }) =>
            e.flgTemplatePadrao &&
            e.oidTemplatePesquisa === state.survey.template
        );

        if (isTemplateValid) {
          inputElement.style.setProperty('color', '#E60050', 'important');
        } else {
          inputElement.style.setProperty('color', 'unset');
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.survey.template, state.survey.enabled, state.survey.templates]);

  return (
    <Row className={styles.root}>
      <Row className={styles.title_content}>
        <Col cols={[12, 12, 6, 6, 6]}>
          <Row>
            <Title>{surveyPrefix.Titulo}</Title>
            {state.data.campos.flgEventTest ? (
              <span
                style={{
                  color: '#e60000',
                  fontSize: '12px',
                  marginTop: '12px',
                }}
              >
                {translation.Assessor.Evento.Cadastro.Etapas.Dados.EventoTeste}
              </span>
            ) : (
              <Fragment />
            )}
          </Row>
        </Col>

        {[SituationValues.AVAILABLE, SituationValues.IN_PREPARATION].includes(
          state.eventStatus!
        ) &&
        state.status !== StepStatus.EM_EDICAO &&
        ![AuthUserPerfilId.PertencerConsulta].includes(perfil!?.id!) ? (
          <Col cols={[12, 12, 6, 6, 6]} end buttonActionsCol>
            <Button
              theme={ButtonThemes.Primary}
              leftIcon={<EditIcon />}
              onClick={() => {
                setEditButtonLoader(true);
                makeEditable(
                  String(match.params.id),
                  () => {
                    setEditButtonLoader(false);
                    setStepStatus(StepStatus.EM_EDICAO);
                    changeState(
                      RegisterContextActions.SET_EVENT_STATUS,
                      SituationValues.EDITABLE
                    );
                  },
                  (error) => {
                    setEditButtonLoader(false);
                    showToast({
                      pauseOnFocusLoss: true,
                      theme: ToastTypes.Error,
                      showStatusBar: true,
                      timeout: 4000,
                      label:
                        translation.Toasts.Errors.Keys[
                          `${
                            (error as AxiosError<Error>).response?.data
                              .message ||
                            (error as AxiosError<Error>).response?.data ||
                            (error as AxiosError<Error>).message
                          }` as keyof typeof translation.Toasts.Errors.Keys
                        ] ?? surveyPrefix.Errors.Default,
                    });
                  }
                );
              }}
              disabled={editButtonLoader}
              loading={editButtonLoader}
            >
              {surveyPrefix.Buttons.Editar}
            </Button>
          </Col>
        ) : undefined}
      </Row>
      <Row>
        <Col>
          <p>{surveyPrefix.PrimeiroSubtitulo}</p>
        </Col>
      </Row>
      <Row>
        <Col>
          <p>{surveyPrefix.SegundoSubtitulo}</p>
        </Col>
      </Row>
      <Row style={{ margin: '12px 0' }}>
        <Col gap={12}>
          <ControlLabel
            control={
              <Checkbox
                disabled={
                  state.status === StepStatus.VISUALIZACAO || completedStep
                }
                onChange={(e: boolean) => {
                  surveyStepSetFilter({ enabled: e });
                  setSent(false);
                }}
                value={state.survey.enabled}
                theme={CheckboxThemes.Classic}
              />
            }
            label={surveyPrefix.Campos.TeraPesquisa}
            labelPosition={ControlLabelPosition.Left}
          />
        </Col>
      </Row>
      <Row>
        <Collapse opened={state.survey.enabled} animateOpacity>
          <Col cols={[6, 6, 6, 6, 6]}>
            <Select
              id='select-template'
              label='Pesquisa'
              placeholder='Selecione a pesquisa que deseja inserir'
              onChange={(e) => surveyStepSetFilter({ template: e })}
              value={state.survey.template}
              clearButton={!completedStep}
              disabled={completedStep}
              options={
                state.survey.templates.map(
                  (item: {
                    nome: string;
                    codigo: number;
                    flgTemplatePadrao: boolean;
                    situacao: string;
                    oidTemplatePesquisa: string;
                  }) => {
                    return {
                      label: item.flgTemplatePadrao
                        ? `${item.nome} (Padrão)`
                        : item.nome,
                      value: item.oidTemplatePesquisa,
                      styles: item.flgTemplatePadrao
                        ? { color: 'rgba(230, 0, 80, 1)' }
                        : {},
                      action: ['DISPONIVEL', 'EM_USO'].includes(
                        item.situacao
                      ) ? (
                        <IconButton
                          icon={<ViewIcon />}
                          onClick={(e) => {
                            e.stopPropagation();

                            const {
                              situacao,
                              flgTemplatePadrao,
                              oidTemplatePesquisa,
                            } = item;
                            const baseUrl = `${
                              getApis().links.pesquisareacao
                            }/template-pesquisa`;

                            let url;

                            if (situacao === 'EM_USO' || flgTemplatePadrao) {
                              url = `${baseUrl}/visualizar/${oidTemplatePesquisa}`;
                            } else if (situacao === 'DISPONIVEL') {
                              url = `${baseUrl}/editar/${oidTemplatePesquisa}`;
                            }

                            if (url) {
                              window.open(url, '_blank');
                            }
                          }}
                        />
                      ) : (
                        <Fragment />
                      ),
                    };
                  }
                ) || []
              }
              error={sent && !!validator && validator.templateIsValid().error}
              helperText={
                sent && !!validator
                  ? validator.templateIsValid().helperText
                  : ''
              }
            />
          </Col>
        </Collapse>
      </Row>
      {/* <Row>
        <Col buttonActionsCol>
          <Button
            leftIcon={isDownloading ? <SyncIcon animated /> : <DownloadIcon />}
            theme={ButtonThemes.Secondary}
            style={{ width: '256px', height: '60px' }}
            onClick={downloadSurvey}
          >
            {surveyPrefix.Buttons.Visualizar}
          </Button>
        </Col>
      </Row> */}
      <Row>
        <Col buttonActionsCol end>
          <Button
            theme={ButtonThemes.Secondary}
            onClick={backStep}
            leftIcon={<ArrowLeftIcon />}
          >
            {surveyPrefix.Buttons.Voltar}
          </Button>
          <Button
            data-testid={'button-save'}
            theme={ButtonThemes.Primary}
            onClick={
              state.status === StepStatus.VISUALIZACAO
                ? () =>
                    history.push(
                      `${AssessorCadastroEventoRoutePath}${match.params.id}/etapas/anexo`
                    )
                : handleSubmit
            }
            loading={loading}
            disabled={loading}
            leftIcon={<CheckCircleIcon />}
          >
            {state.status === StepStatus.VISUALIZACAO
              ? surveyPrefix.Buttons.Prosseguir
              : surveyPrefix.Buttons.Salvar}
          </Button>
        </Col>
      </Row>
    </Row>
  );
}
